/** Catalog translation helpers aligned with backend resolution rules. */

import {
  SUPPORTED_LOCALES,
  resolveLocale,
  type SupportedLocale,
} from "@/lib/locales";
import type { CompleteTranslations, Translations } from "@/types";

export type TranslationFormValues = Partial<
  Record<SupportedLocale, string | null>
>;
export type TranslationLike = Translations | TranslationFormValues;

export const ACTIVITY_DESCRIPTION_MAX_LENGTH = 450;
export const ACTIVITY_NAME_MAX_LENGTH = 255;

export function findTranslationOverMaxLength(
  values: TranslationLike,
  maxLength: number,
): SupportedLocale | undefined {
  for (const code of SUPPORTED_LOCALES) {
    const value = values[code];
    if (value && value.length > maxLength) {
      return code;
    }
  }
  return undefined;
}

export function findMissingTranslations(
  values: TranslationLike,
): SupportedLocale[] {
  return SUPPORTED_LOCALES.filter((code) => !values[code]?.trim());
}

export function hasPartialTranslations(values: TranslationLike): boolean {
  const filled = SUPPORTED_LOCALES.filter((code) =>
    Boolean(values[code]?.trim()),
  ).length;
  return filled > 0 && filled < SUPPORTED_LOCALES.length;
}

export function buildRequiredTranslations(
  values: TranslationFormValues,
): CompleteTranslations {
  return Object.fromEntries(
    SUPPORTED_LOCALES.map((code) => [code, values[code]?.trim() ?? ""]),
  ) as CompleteTranslations;
}

/** Sparse map with null for empty locales (e.g. draft carousel titles). */
export function buildSparseTranslations(
  values: TranslationFormValues,
): Translations {
  return Object.fromEntries(
    SUPPORTED_LOCALES.map((code) => [code, values[code]?.trim() || null]),
  ) as Translations;
}

/** Null when every locale is empty; otherwise a sparse translation map. */
export function buildOptionalTranslations(
  values: TranslationFormValues,
): Translations | null {
  if (!SUPPORTED_LOCALES.some((code) => values[code]?.trim())) {
    return null;
  }
  return buildSparseTranslations(values);
}

/** Item descriptions: null when empty, otherwise all locales required. */
export function buildItemDescriptionTranslations(
  values: TranslationFormValues,
): CompleteTranslations | null {
  if (!SUPPORTED_LOCALES.some((code) => values[code]?.trim())) {
    return null;
  }
  return buildRequiredTranslations(values);
}

export function areTranslationsEqual(
  current: TranslationLike,
  baseline: TranslationLike | null | undefined,
): boolean {
  return SUPPORTED_LOCALES.every(
    (code) => (current[code]?.trim() ?? "") === (baseline?.[code]?.trim() ?? ""),
  );
}

export function findAutoTranslateSourceLanguage(
  values: TranslationFormValues,
  preferred?: SupportedLocale,
): SupportedLocale | undefined {
  if (preferred) {
    const resolved = resolveLocale(preferred);
    if (values[resolved]?.trim()) {
      return resolved;
    }
  }
  return SUPPORTED_LOCALES.find((code) => Boolean(values[code]?.trim()));
}

export function resolveCatalogTranslation(
  translations: TranslationLike | null | undefined,
  requestedLanguage: string,
  fallback = "",
): string {
  const requested = resolveLocale(requestedLanguage);
  return translations?.[requested]?.trim() || fallback;
}
